import { fromBase64, toBase64 } from 'js-base64';

import { Cloud } from '@cast/shared-types';
import { EdgeConfiguration, EdgeConfigurationUpdate } from '@cast/types-omni';

import { KeyVal, reduceKeyVal } from 'core/forms';
import { EdgeLocationProvider } from 'features/cluster/autoscaler/edge-locations/types';

import { EdgeConfigurationFormValues } from './validate.schema';

const getTags = (
  values: EdgeConfigurationFormValues
): Record<string, string> | undefined => reduceKeyVal(values.tags);

const getBootDiskSizeGib = (
  values: EdgeConfigurationFormValues
): number | undefined =>
  values.bootDiskSizeGib !== ''
    ? parseInt(values.bootDiskSizeGib, 10)
    : undefined;

const encodeSshPublicKey = (sshPublicKey: string): string => {
  const trimmedSshPublicKey = sshPublicKey.trim();
  return trimmedSshPublicKey ? toBase64(trimmedSshPublicKey) : '';
};

export const toEdgeConfigurationApiModel = (
  values: EdgeConfigurationFormValues,
  provider?: EdgeLocationProvider,
  isEditMode?: boolean
): EdgeConfiguration | EdgeConfigurationUpdate => {
  const payload: EdgeConfiguration | EdgeConfigurationUpdate = isEditMode
    ? {}
    : {
        name: values.name,
      };

  const tags = getTags(values);
  const bootDiskSizeGib = getBootDiskSizeGib(values);
  const commonProviderFields = {
    ...(bootDiskSizeGib !== undefined && { bootDiskSizeGib }),
    ...(values.imageId && { imageId: values.imageId }),
  };

  if (values.criSocketPath) {
    payload.cri = { socket: values.criSocketPath };
  }

  if (values.initScript) {
    payload.userDataBase64 = toBase64(values.initScript);
  }

  payload.sshPublicKey = encodeSshPublicKey(values.sshPublicKey);

  if (provider === Cloud.aws) {
    return {
      ...payload,
      aws: {
        ...commonProviderFields,
        ...(tags && { tags }),
      },
    };
  }

  if (provider === Cloud.gcp) {
    return {
      ...payload,
      gcp: {
        ...commonProviderFields,
        ...(tags && { labels: tags }),
      },
    };
  }

  if (provider === Cloud.oracle) {
    return {
      ...payload,
      oci: {
        ...commonProviderFields,
        ...(tags && { tags }),
      },
    };
  }

  if (provider === Cloud.nebius) {
    return {
      ...payload,
      nebius: {
        ...commonProviderFields,
        gpuCluster: values.gpuCluster || '',
        reservationIds: values.reservationIds,
        labels: tags || {},
      },
    };
  }

  return payload;
};

const expandTags = (providerData?: {
  tags?: Record<string, string>;
  labels?: Record<string, string>;
}): KeyVal[] => {
  if (!providerData) return [{ key: '', value: '' }];

  const entries = Object.entries(
    providerData.tags || providerData.labels || {}
  );
  if (entries.length === 0) return [{ key: '', value: '' }];

  return entries.map(([key, value]) => ({ key, value }));
};

export const fromEdgeConfigurationApiModel = (
  config: EdgeConfiguration
): EdgeConfigurationFormValues => {
  const providerData = config.aws || config.gcp || config.oci || config.nebius;

  return {
    edgeLocationId: config.edgeLocationId || '',
    name: config.name || '',
    tags: expandTags(providerData),
    bootDiskSizeGib: providerData?.bootDiskSizeGib?.toString() || '',
    imageId: providerData?.imageId || '',
    criSocketPath: config.cri?.socket || '',
    default: config.default || false,
    initScript: config.userDataBase64 ? fromBase64(config.userDataBase64) : '',
    sshPublicKey: config.sshPublicKey ? fromBase64(config.sshPublicKey) : '',
    gpuCluster: config.nebius?.gpuCluster || '',
    reservationIds: config.nebius?.reservationIds || [],
  };
};
